export type BillingMigrationStatus =
  | 'pending'
  | 'parsing'
  | 'preview_ready'
  | 'importing'
  | 'completed'
  | 'failed'
  | 'cancelled';

export interface BillingMigrationImportStats {
  files_total?: number;
  files_837?: number;
  files_835?: number;
  files_error?: number;
  total_claims?: number;
  total_remittances?: number;
  total_remittance_claims?: number;
  claims_created?: number;
  claims_skipped?: number;
  remittances_created?: number;
  remittances_skipped?: number;
  remittance_claims_matched?: number;
  claim_errors?: number;
  remittance_errors?: number;
}

export interface BillingMigrationPreviewData {
  summary: {
    total_claims: number;
    total_eras: number;
    total_era_claims: number;
    unique_npis: number;
    unique_patients: number;
  };
  providers: {
    billing_npis: string[];
    rendering_npis: string[];
  };
  sample_claims: Array<{
    pcn: string;
    charge_cents: number;
    patient: string;
    payer: string;
    cpt_codes: string[];
    service_date: string;
    rendering_npi: string;
  }>;
  sample_eras: Array<{
    trace_number: string;
    payer: string;
    payment_cents: number;
    claim_count: number;
    sample_pcns: string[];
  }>;
}

export const BILLING_MIGRATION_SOURCE_SYSTEMS = [
  { value: 'collabmd', label: 'CollabMD' },
  { value: 'claimmd', label: 'ClaimMD' },
  { value: 'tebra', label: 'Tebra (Kareo)' },
  { value: 'athenahealth', label: 'athenahealth' },
  { value: 'advancedmd', label: 'AdvancedMD' },
  { value: 'other', label: 'Other' },
] as const;

export type SourceSystemValue = typeof BILLING_MIGRATION_SOURCE_SYSTEMS[number]['value'];

export const getStatusLabel = (status: string): string => {
  switch (status.toLowerCase()) {
    case 'pending':
      return 'Pending';
    case 'parsing':
      return 'Parsing Files';
    case 'preview_ready':
      return 'Ready for Review';
    case 'importing':
      return 'Importing';
    case 'completed':
      return 'Completed';
    case 'failed':
      return 'Failed';
    case 'cancelled':
      return 'Cancelled';
    default:
      return status;
  }
};

export const getStatusColor = (
  status: string,
): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
  switch (status.toLowerCase()) {
    case 'pending':
      return 'default';
    case 'parsing':
      return 'info';
    case 'preview_ready':
      return 'warning';
    case 'importing':
      return 'info';
    case 'completed':
      return 'success';
    case 'failed':
      return 'error';
    case 'cancelled':
      return 'default';
    default:
      return 'default';
  }
};

export const isSessionProcessing = (status: string): boolean => {
  const s = status.toLowerCase();
  return s === 'pending' || s === 'parsing' || s === 'importing';
};

export const getPollingInterval = (status: string): number | false => {
  switch (status.toLowerCase()) {
    case 'pending':
      return 30000;
    case 'parsing':
      return 3000;
    case 'importing':
      return 3000;
    case 'preview_ready':
    case 'completed':
    case 'failed':
    case 'cancelled':
      return false;
    default:
      return 30000;
  }
};

export const formatCents = (cents: number): string => {
  return `$${(cents / 100).toFixed(2)}`;
};
